




























































































import LazyHydrate from 'vue-lazy-hydration';
import {
  SfButton,
  SfHeading,
  SfLoader,
  SfPrice,
  SfRating,
  SfAlert
} from '@storefront-ui/vue';
import {
  ref,
  computed,
  defineComponent,
  PropType,
  toRef,
} from '@nuxtjs/composition-api';

import {
  getName as getProductName,
  getPrice as getProductPrice,
  getStockStatus as getStockStatus,
} from '~/modules/catalog/product/getters/productGetters';

import {
  getTotalReviews,
  getAverageRating,
} from '~/modules/review/getters/reviewGetters';

import useWishlist from '~/modules/wishlist/composables/useWishlist';
import SvgImage from '~/components/General/SvgImage.vue';
import HTMLContent from '~/components/HTMLContent.vue';
import AddToWishlist from '~/components/AddToWishlist.vue';
import { useUser } from '~/modules/customer/composables/useUser';
import { Product } from '~/modules/catalog/product/types';
import { useCart } from '~/modules/checkout/composables/useCart';
import ProductTabs from '~/modules/catalog/product/components/tabs/ProductTabs.vue';
import { useProductGallery } from '~/modules/catalog/product/composables/useProductGallery';
import { TabsConfig, useProductTabs } from '~/modules/catalog/product/composables/useProductTabs';
import ProductGallery from '~/modules/catalog/product/components/ProductGallery.vue';
import SfAddToCart from '~/modules/catalog/product/components/molecules/SfAddToCart.vue';
import CustomizableOptions from '~/modules/catalog/product/components/CustomizableOptions.vue';
import type { WithTypename } from '~/modules/catalog/product/types';
import type { SimpleProduct } from '~/modules/GraphQL/types';
import { ProductStockStatus } from '~/modules/GraphQL/types';
import HomeWatermark from '~/components/Headless/Watermarks/HomeWatermark.vue';
import { setCursorNativeWaiting } from '~/helpers/headless/helpers';
import {
  useUiState,
} from '~/composables';

export default defineComponent({
  name: 'SimpleProduct',
  components: {
    HTMLContent,
    LazyHydrate,
    SfAddToCart,
    SfButton,
    SfHeading,
    SfLoader,
    SfPrice,
    SfRating,
    SfAlert,
    AddToWishlist,
    SvgImage,
    ProductTabs,
    ProductGallery,
    CustomizableOptions,
    HomeWatermark,
    RelatedProducts: () => import('~/modules/catalog/product/components/RelatedProducts.vue')
  },
  transition: 'fade',
  props: {
    product: {
      type: [Object, null] as PropType<WithTypename<SimpleProduct>>,
      default: null,
    },
    isFetching: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['changeOptions', 'validation'],
  setup(props) {
    const qty = ref(1);
    const product = toRef(props, 'product');
    const { addItem, error: cartError, loading: isCartLoading, canAddToCart,} = useCart();
    const { productGallery, imageSizes } = useProductGallery(product);
    const { isAuthenticated } = useUser();
    const { addOrRemoveItem, isInWishlist } = useWishlist();
    const { activeTab, setActiveTab, openNewReviewTab } = useProductTabs();

    const productShortDescription = computed(
      () => props.product?.short_description?.html || '',
    );
    const updatedPrice = ref(0);
    const productPrice = ref(null);
    const productSpecialPrice = ref(null);
    const initialPrice = getProductPrice(props.product).regular;
    const initialSpecialPrice = getProductPrice(props.product).special ? getProductPrice(props.product).special : null;
    productPrice.value = getProductPrice(props.product).regular;
    if (getProductPrice(props.product).special) {
      productSpecialPrice.value = getProductPrice(props.product).special;
    }

    const totalReviews = computed(() => getTotalReviews(props.product));
    const averageRating = computed(() => getAverageRating(props.product));
    const customizableOptions = computed(() => props.product.options);
    const addToCartError = computed(() => cartError.value?.addItem?.message);
    const stockMessage = computed(() => (getStockStatus(props.product) === ProductStockStatus.InStock ? '' : 'Out Of Stock'));
    const selectedOptions = ref({});
    const productOptions = [];

    const updatePrice = (options: Array<string>) => {
      updatedPrice.value = 0;
      options.forEach(option => {
        const priceNumber = parseInt(option[2]);
        updatedPrice.value += priceNumber;
      });

      productPrice.value = getProductPrice(props.product).regular + updatedPrice.value;
      if (getProductPrice(props.product).special) {
        productSpecialPrice.value = getProductPrice(props.product).special + updatedPrice.value;
      }
    }

    const updateProductOptions = async (label: String, value: String, price: String) => {
      if (productOptions.length == 0) {
        productOptions.push([label, value, price]);
      } else {
        let push = true;
        productOptions.forEach(option => {
          if (option[0] == label) {
            option[1] = value;
            option[2] = price;
            push = false;
          }
        });
        if (push) productOptions.push([label, value, price]);
       }
      selectedOptions.value = productOptions;
      updatePrice(productOptions);
    }

    const cartState = ref(false);
    const setAddToCart = (state) => {
      cartState.value = state;
    }

    // Toggle sidebar
    const { toggleCartSidebar, isCartSidebarOpen } = useUiState();

    const addToCart = async (qty) => {
      setCursorNativeWaiting(false);
      const enteredOptionsData =  Object.keys(selectedOptions.value).map((selectedOptionKey) => {
        const selectedOption = selectedOptions.value[selectedOptionKey];
        return {
          uid: btoa(`custom-option/${selectedOption[0]}`),
          value: selectedOption[1],
        };
      });

      await addItem({
        product: {
          ...props.product,
          options: enteredOptionsData,
        } as SimpleProduct,
        quantity: qty,
      });
      toggleCartSidebar();
      setCursorNativeWaiting(isCartSidebarOpen.value);
    };

    return {
      addItem,
      addItemToWishlist: addOrRemoveItem,
      averageRating,
      totalReviews,
      canAddToCart,
      isAuthenticated,
      isInWishlist: computed(() => isInWishlist({ product: props.product })),
      isCartLoading,
      productShortDescription,
      productGallery,
      getProductName,
      getStockStatus,
      stockMessage,
      productPrice,
      productSpecialPrice,
      qty,
      imageSizes,
      setActiveTab,
      openNewReviewTab,
      activeTab,
      TabsConfig,
      customizableOptions,
      updateProductOptions,
      addToCart,
      setAddToCart,
      cartState,
      addToCartError,
      initialPrice,
      initialSpecialPrice
    };
  },
});
